'use client';

import { useState, Fragment } from 'react';
import ComboBox, { ComboBoxOptionsSlot } from '@pixum/combobox';
import Menu from '@pixum/menu';
import MenuItem, { LeadingType } from '@pixum/menu-item';
import Divider from '@pixum/divider';
import type { RevocableOrder } from 'data-layer';
import type { CanceledOrder } from '../../../../types';
import styles from './ProductMultiSelection.module.css';
import { MultiSelectionFallbackContent } from './MultiSelectionFallbackContent';

interface ProductMultiSelectionProps {
  orders: RevocableOrder[];
  canceledOrders: CanceledOrder[];
  placeholder: string;
  label: string;
  fallbackText: string;
  summaryCounts: string;
  orderItemLabel: string;
  disabled?: boolean;
  loading?: boolean;
  onChange?: (canceledOrders: CanceledOrder[]) => void;
  helperText?: string;
  hasError?: boolean;
}

/**
 * Multi-select ComboBox for choosing which add-on products to include in a cancellation request.
 * Groups products by order with a labelled stripe header. Supports standalone add-ons and add-ons
 * nested under a parent product. The display value summarises the selection and collapses to
 * per-order counts when multiple products are selected.
 *
 * @param props.orders - Revocable orders with eligible add-ons, filtered by selected order IDs
 * @param props.canceledOrders - Currently selected articles per order, controlled by the parent
 * @param props.placeholder - Placeholder text shown when no products are selected
 * @param props.label - Accessible label for the ComboBox and its options list
 * @param props.fallbackText - Message shown when no selectable products are available
 * @param props.summaryCounts - Summary template per order; supports {orderId} and {count} placeholders
 * @param props.orderItemLabel - Label template for each order stripe header; supports {orderId} placeholder
 * @param props.disabled - Disables the ComboBox when the user is not authenticated
 * @param props.loading - Shows a loading state while orders are being refreshed
 * @param props.onChange - Callback fired with the updated list of canceled orders and their articles
 */
export function ProductMultiSelection(props: ProductMultiSelectionProps) {
  const {
    orders,
    orderItemLabel,
    label,
    placeholder,
    fallbackText,
    summaryCounts,
    disabled,
    loading,
    canceledOrders,
    onChange,
    helperText,
    hasError,
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const formatValue = (): string => {
    const totalSelected = canceledOrders.reduce((sum, co) => sum + co.articles.length, 0);

    if (totalSelected === 1) {
      const co = canceledOrders[0];
      const article = co.articles[0];

      if (!article) return '';

      return `${article.productName} (#${co.orderId})`;
    }

    return canceledOrders
      .filter(co => co.articles.length > 0)
      .map(co => summaryCounts.replace('{orderId}', String(co.orderId)).replace('{count}', String(co.articles.length)))
      .join(', ');
  };

  const isSelected = (orderId: number, productId: number): boolean =>
    canceledOrders.some(co => co.orderId === orderId && co.articles.some(a => a.productId === productId));

  const toggle = (orderId: number, productId: number, productName: string, price: string) => {
    const article = { productId, productName, price };
    const isDeselecting = isSelected(orderId, productId);
    const orderExists = canceledOrders.some(order => order.orderId === orderId);

    let updated: CanceledOrder[];

    if (isDeselecting) {
      updated = canceledOrders
        .map(order =>
          order.orderId === orderId
            ? { ...order, articles: order.articles.filter(a => a.productId !== productId) }
            : order,
        )
        .filter(order => order.articles.length > 0);
    } else if (orderExists) {
      updated = canceledOrders.map(order =>
        order.orderId === orderId ? { ...order, articles: [...order.articles, article] } : order,
      );
    } else {
      updated = [...canceledOrders, { orderId, articles: [article] }];
    }

    onChange?.(updated);
  };

  const hasAddOns = orders.some(o => o.products.some(p => p.addOns.length > 0) || o.standaloneAddOns.length > 0);

  const options = (
    <ComboBoxOptionsSlot className={styles.options} aria-multiselectable="true" aria-label={label} tag="ul">
      {orders.map((order, orderIndex) => (
        <Fragment key={order.userOrderId}>
          <li className={styles.orderStripe}>{orderItemLabel.replace('{orderId}', String(order.userOrderId))}</li>

          {order.products.map(product => (
            <Fragment key={product.productId}>
              <MenuItem
                className={styles.productGroupLabel}
                role="group"
                tag="li"
                text={product.productName}
                subtext={product.productSubTitle}
              />
              <Divider />
              <Menu className={styles.productGroup} tag="ul" aria-label={product.productName} role="group">
                {product.addOns.map(addOn => {
                  const selected = isSelected(addOn.orderId, addOn.productId);

                  return (
                    <MenuItem
                      className={styles.addOnItem}
                      role="option"
                      key={`${addOn.orderId}-${addOn.productId}`}
                      tag="li"
                      text={addOn.productName}
                      leadingProps={{ type: LeadingType.CheckBox }}
                      subItemProps={{ text: addOn.price }}
                      selected={selected}
                      aria-selected={selected}
                      onClick={() => toggle(addOn.orderId, addOn.productId, addOn.productName, addOn.price)}
                    />
                  );
                })}
              </Menu>
            </Fragment>
          ))}

          {order.standaloneAddOns.length > 0 && (
            <Menu className={styles.productGroup} tag="ul" aria-label={label} role="group">
              {order.standaloneAddOns.map(addOn => {
                const selected = isSelected(addOn.orderId, addOn.productId);

                return (
                  <MenuItem
                    className={styles.standaloneItem}
                    role="option"
                    key={`${addOn.orderId}-${addOn.productId}`}
                    tag="li"
                    text={addOn.productName}
                    leadingProps={{ type: LeadingType.CheckBox }}
                    subItemProps={{ text: addOn.price }}
                    selected={selected}
                    aria-selected={selected}
                    onClick={() => toggle(addOn.orderId, addOn.productId, addOn.productName, addOn.price)}
                  />
                );
              })}
            </Menu>
          )}

          {orderIndex < orders.length - 1 && <Divider />}
        </Fragment>
      ))}
    </ComboBoxOptionsSlot>
  );

  const fallback = (
    <ComboBoxOptionsSlot tag="div" className={styles.fallback} aria-live="polite">
      <MultiSelectionFallbackContent text={fallbackText}></MultiSelectionFallbackContent>
    </ComboBoxOptionsSlot>
  );

  return (
    <ComboBox
      disabled={disabled}
      loading={loading}
      label={label}
      headline={label}
      placeholder={placeholder}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onClick={() => setIsOpen(prev => !prev)}
      options={[]}
      value={formatValue()}
      withSearch={false}
      optionsSlot={hasAddOns ? options : fallback}
      helperText={helperText}
      hasError={hasError}
    />
  );
}
